import {
  and,
  documents,
  eq,
  generateULID,
  inArray,
  processing_config_snapshots,
  processing_outbox,
  processing_review_decisions,
  processing_review_tasks,
  processing_runs,
  processing_stage_receipts,
  processing_work_items,
  sql,
  type PrimaryDB,
  type ProcessingRunStatus,
} from "@ocr-monorepo/db";

export const DOCUMENT_PROCESSING_PIPELINE_ID = "document-processing";
export const DOCUMENT_PROCESSING_PIPELINE_VERSION = 1;

export type ProcessingTrigger =
  | "upload"
  | "email"
  | "api"
  | "dashboard"
  | "connector"
  | "reanalyze";

export type ProcessingRouteSnapshot =
  | { kind: "automatic" }
  | {
      kind: "splitter";
      splitterId: string;
      splitterVersionId: string;
      testOnly?: boolean;
    }
  | {
      kind: "table";
      tableId: string;
      tableName: string;
      destinationId: string;
    };

export type ProcessingConfigSnapshot = {
  pipelineDefinitionId: typeof DOCUMENT_PROCESSING_PIPELINE_ID;
  pipelineVersion: typeof DOCUMENT_PROCESSING_PIPELINE_VERSION;
  route: ProcessingRouteSnapshot;
  stages: readonly [
    "admit",
    "preflight",
    "understand",
    "route",
    "segment",
    "plan",
    "extract",
    "normalize",
    "persist",
    "settle",
    "finalize",
  ];
};

export type EnsureProcessingRunInput = {
  documentId: number;
  userId: string;
  projectId: number;
  trigger: ProcessingTrigger;
  route: ProcessingRouteSnapshot;
  idempotencyKey: string;
  priority?: number;
  usageReservationId?: string;
  /** Inline stages publish admission atomically instead of requiring Workflow recovery. */
  admissionMode?: "workflow" | "inline";
  inlineInstanceId?: string;
};

export type ProcessingRunIdentity = {
  runId: string;
  configSnapshotId: string;
  inserted: boolean;
};

function normalizeJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeJson);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, normalizeJson(entry)]),
    );
  }
  return value;
}

export function stableJson(value: unknown): string {
  return JSON.stringify(normalizeJson(value));
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export function buildProcessingConfigSnapshot(
  route: ProcessingRouteSnapshot,
): ProcessingConfigSnapshot {
  return {
    pipelineDefinitionId: DOCUMENT_PROCESSING_PIPELINE_ID,
    pipelineVersion: DOCUMENT_PROCESSING_PIPELINE_VERSION,
    route,
    stages: [
      "admit",
      "preflight",
      "understand",
      "route",
      "segment",
      "plan",
      "extract",
      "normalize",
      "persist",
      "settle",
      "finalize",
    ],
  };
}

export async function ensureProcessingRun(
  db: PrimaryDB,
  input: EnsureProcessingRunInput,
): Promise<ProcessingRunIdentity> {
  const existing = await db
    .select({
      id: processing_runs.id,
      configSnapshotId: processing_runs.config_snapshot_id,
    })
    .from(processing_runs)
    .where(eq(processing_runs.idempotency_key, input.idempotencyKey))
    .limit(1);
  if (existing[0]) {
    return {
      runId: existing[0].id,
      configSnapshotId: existing[0].configSnapshotId,
      inserted: false,
    };
  }

  const snapshot = buildProcessingConfigSnapshot(input.route);
  const checksum = await sha256Hex(stableJson(snapshot));
  const snapshotId = generateULID();
  await db
    .insert(processing_config_snapshots)
    .values({
      id: snapshotId,
      project_id: input.projectId,
      pipeline_definition_id: DOCUMENT_PROCESSING_PIPELINE_ID,
      pipeline_version: DOCUMENT_PROCESSING_PIPELINE_VERSION,
      checksum,
      snapshot,
      createdAt: new Date(),
    })
    .onConflictDoNothing({
      target: [
        processing_config_snapshots.project_id,
        processing_config_snapshots.checksum,
      ],
    });

  const [storedSnapshot] = await db
    .select({ id: processing_config_snapshots.id })
    .from(processing_config_snapshots)
    .where(
      and(
        eq(processing_config_snapshots.project_id, input.projectId),
        eq(processing_config_snapshots.checksum, checksum),
      ),
    )
    .limit(1);
  if (!storedSnapshot) throw new Error("Unable to persist processing configuration");

  const runId = generateULID();
  const eventId = `processing-run:${runId}:admit`;
  const now = new Date();
  const inlineAdmission = input.admissionMode === "inline";
  try {
    await db.batch([
      db.insert(processing_runs).values({
        id: runId,
        document_id: input.documentId,
        user_id: input.userId,
        project_id: input.projectId,
        config_snapshot_id: storedSnapshot.id,
        pipeline_definition_id: DOCUMENT_PROCESSING_PIPELINE_ID,
        pipeline_version: DOCUMENT_PROCESSING_PIPELINE_VERSION,
        trigger: input.trigger,
        priority: input.priority ?? 0,
        status: inlineAdmission ? "running" : "queued",
        current_stage: inlineAdmission ? "segment" : "admit",
        workflow_instance_id: inlineAdmission ? input.inlineInstanceId : null,
        idempotency_key: input.idempotencyKey,
        usage_reservation_id: input.usageReservationId,
        createdAt: now,
        updatedAt: now,
      }),
      db.insert(processing_outbox).values({
        id: generateULID(),
        run_id: runId,
        event_id: eventId,
        event_type: "processing.run.admitted",
        payload_version: 1,
        destination: inlineAdmission
          ? "processing-inline"
          : "processing-admission",
        payload: {
          commandId: eventId,
          runId,
          documentId: input.documentId,
          configSnapshotId: storedSnapshot.id,
        },
        status: inlineAdmission ? "published" : "pending",
        publish_attempts: inlineAdmission ? 1 : 0,
        publishedAt: inlineAdmission ? now : null,
        createdAt: now,
        updatedAt: now,
      }),
    ]);
  } catch (error) {
    const [raced] = await db
      .select({
        id: processing_runs.id,
        configSnapshotId: processing_runs.config_snapshot_id,
      })
      .from(processing_runs)
      .where(eq(processing_runs.idempotency_key, input.idempotencyKey))
      .limit(1);
    if (!raced) throw error;
    return {
      runId: raced.id,
      configSnapshotId: raced.configSnapshotId,
      inserted: false,
    };
  }

  return { runId, configSnapshotId: storedSnapshot.id, inserted: true };
}

export async function transitionProcessingRun(
  db: PrimaryDB,
  input: {
    runId?: string;
    status: ProcessingRunStatus;
    stage: string;
    workflowInstanceId?: string;
    jobId?: number;
    failureCode?: string | null;
    failureMessage?: string | null;
  },
): Promise<void> {
  if (!input.runId) return;
  const now = new Date();
  const nowEpochSeconds = Math.floor(now.getTime() / 1_000);
  const terminal = [
    "succeeded",
    "partially_succeeded",
    "failed",
    "cancelled",
  ].includes(input.status);
  const allowedSources: Record<ProcessingRunStatus, ProcessingRunStatus[]> = {
    queued: ["queued"],
    running: ["queued", "running", "waiting_for_review"],
    waiting_for_review: ["queued", "running", "waiting_for_review"],
    cancelling: ["queued", "running", "waiting_for_review", "cancelling"],
    succeeded: ["running"],
    partially_succeeded: ["running"],
    failed: ["queued", "running", "waiting_for_review", "cancelling"],
    cancelled: ["queued", "waiting_for_review", "cancelling"],
  };
  await db
    .update(processing_runs)
    .set({
      status: input.status,
      current_stage: input.stage,
      workflow_instance_id: input.workflowInstanceId,
      job_id: input.jobId,
      failure_code: input.failureCode,
      failure_message: input.failureMessage,
      startedAt:
        input.status === "running"
          ? sql<Date>`coalesce(${processing_runs.startedAt}, ${nowEpochSeconds})`
          : undefined,
      completedAt: terminal ? now : undefined,
      lastHeartbeatAt: now,
      updatedAt: now,
    })
    .where(
      and(
        eq(processing_runs.id, input.runId),
        inArray(processing_runs.status, allowedSources[input.status]),
      ),
    );
}

export async function markProcessingDispatchPublished(
  db: PrimaryDB,
  input: { runId?: string; workflowInstanceId: string; stage: string },
): Promise<void> {
  if (!input.runId) return;
  const now = new Date();
  await db.batch([
    db
      .update(processing_runs)
      .set({
        workflow_instance_id: input.workflowInstanceId,
        current_stage: input.stage,
        lastHeartbeatAt: now,
        updatedAt: now,
      })
      .where(eq(processing_runs.id, input.runId)),
    db
      .update(processing_outbox)
      .set({
        status: "published",
        publish_attempts: 1,
        publishedAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(processing_outbox.run_id, input.runId),
          eq(processing_outbox.event_type, "processing.run.admitted"),
          eq(processing_outbox.status, "pending"),
        ),
      ),
  ]);
}

export async function recordStageReceipt(
  db: PrimaryDB,
  input: {
    runId?: string;
    stage: string;
    inputHash: string;
    status: "succeeded" | "failed";
    scopeId?: string;
    resultHash?: string;
    provider?: string;
    model?: string;
    metrics?: Record<string, unknown>;
    errorCode?: string;
    errorMessage?: string;
    retryClass?: "transient" | "permanent" | "reviewable";
    startedAt: Date;
    completedAt?: Date;
  },
): Promise<void> {
  if (!input.runId) return;
  await db
    .insert(processing_stage_receipts)
    .values({
      id: generateULID(),
      run_id: input.runId,
      stage_name: input.stage,
      stage_version: 1,
      scope_id: input.scopeId ?? "document",
      input_hash: input.inputHash,
      result_hash: input.resultHash,
      status: input.status,
      provider: input.provider,
      model: input.model,
      metrics: input.metrics,
      error_code: input.errorCode,
      error_message: input.errorMessage,
      retry_class: input.retryClass,
      startedAt: input.startedAt,
      completedAt: input.completedAt ?? new Date(),
      createdAt: new Date(),
    })
    .onConflictDoNothing({
      target: [
        processing_stage_receipts.run_id,
        processing_stage_receipts.stage_name,
        processing_stage_receipts.stage_version,
        processing_stage_receipts.scope_id,
        processing_stage_receipts.input_hash,
      ],
    });
}

export async function ensureProcessingWorkItem(
  db: PrimaryDB,
  input: {
    runId: string;
    destinationId: string;
    tableId: string;
    schemaRevisionId: string;
    segmentId?: string;
    status?: "queued" | "running";
  },
): Promise<string> {
  const workItemId = generateULID();
  const segmentId = input.segmentId ?? "document";
  await db
    .insert(processing_work_items)
    .values({
      id: workItemId,
      run_id: input.runId,
      segment_id: segmentId,
      destination_id: input.destinationId,
      table_id: input.tableId,
      schema_revision_id: input.schemaRevisionId,
      status: input.status ?? "running",
      attempt_count: input.status === "queued" ? 0 : 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    })
    .onConflictDoNothing({
      target: [
        processing_work_items.run_id,
        processing_work_items.segment_id,
        processing_work_items.destination_id,
        processing_work_items.table_id,
        processing_work_items.schema_revision_id,
      ],
    });
  const [stored] = await db
    .select({ id: processing_work_items.id })
    .from(processing_work_items)
    .where(
      and(
        eq(processing_work_items.run_id, input.runId),
        eq(processing_work_items.segment_id, segmentId),
        eq(processing_work_items.destination_id, input.destinationId),
        eq(processing_work_items.table_id, input.tableId),
        eq(processing_work_items.schema_revision_id, input.schemaRevisionId),
      ),
    )
    .limit(1);
  if (!stored) throw new Error("Unable to persist processing work item");
  return stored.id;
}

export async function completeProcessingWorkItem(
  db: PrimaryDB,
  input: {
    runId: string;
    succeeded: boolean;
    resultHash?: string;
    failureCode?: string | null;
    segmentId?: string;
  },
): Promise<void> {
  await db
    .update(processing_work_items)
    .set({
      status: input.succeeded ? "succeeded" : "failed",
      result_hash: input.resultHash,
      failure_code: input.failureCode,
      updatedAt: new Date(),
    })
    .where(
      input.segmentId
        ? and(
            eq(processing_work_items.run_id, input.runId),
            eq(processing_work_items.segment_id, input.segmentId),
          )
        : eq(processing_work_items.run_id, input.runId),
    );
}

export async function startProcessingWorkItem(
  db: PrimaryDB,
  input: { runId: string; segmentId?: string },
): Promise<void> {
  const now = new Date();
  await db
    .update(processing_work_items)
    .set({
      status: "running",
      attempt_count: sql<number>`${processing_work_items.attempt_count} + 1`,
      failure_code: null,
      updatedAt: now,
    })
    .where(
      input.segmentId
        ? and(
            eq(processing_work_items.run_id, input.runId),
            eq(processing_work_items.segment_id, input.segmentId),
          )
        : eq(processing_work_items.run_id, input.runId),
    );
}

export async function finalizeProcessingRunFromWorkItems(
  db: PrimaryDB,
  input: { runId: string; workflowInstanceId?: string },
): Promise<{ terminal: boolean; status: ProcessingRunStatus }> {
  const items = await db
    .select({ status: processing_work_items.status })
    .from(processing_work_items)
    .where(eq(processing_work_items.run_id, input.runId));
  const terminalStatuses = new Set(["succeeded", "failed", "cancelled"]);
  if (items.length === 0 || items.some((item) => !terminalStatuses.has(item.status))) {
    return { terminal: false, status: "running" };
  }

  const succeeded = items.filter((item) => item.status === "succeeded").length;
  const [openReview] = await db
    .select({ id: processing_review_tasks.id })
    .from(processing_review_tasks)
    .where(
      and(
        eq(processing_review_tasks.run_id, input.runId),
        eq(processing_review_tasks.status, "open"),
      ),
    )
    .limit(1);
  if (openReview) {
    await transitionProcessingRun(db, {
      runId: input.runId,
      status: "waiting_for_review",
      stage: "route",
      workflowInstanceId: input.workflowInstanceId,
    });
    return { terminal: false, status: "waiting_for_review" };
  }
  const status: ProcessingRunStatus =
    succeeded === items.length
      ? "succeeded"
      : succeeded > 0
        ? "partially_succeeded"
        : "failed";
  const [run] = await db
    .select({ documentId: processing_runs.document_id })
    .from(processing_runs)
    .where(eq(processing_runs.id, input.runId))
    .limit(1);
  await transitionProcessingRun(db, {
    runId: input.runId,
    status,
    stage: "finalize",
    workflowInstanceId: input.workflowInstanceId,
    failureCode: status === "succeeded" ? null : "SEGMENT_EXTRACTION_FAILED",
    failureMessage:
      status === "succeeded"
        ? null
        : `${items.length - succeeded} of ${items.length} segment extractions failed`,
  });
  if (run) {
    await db
      .update(documents)
      .set({
        status: succeeded > 0 ? "completed" : "failed",
        updatedAt: new Date(),
      })
      .where(eq(documents.id, run.documentId));
  }
  return { terminal: true, status };
}

export async function ensureRoutingReviewTask(
  db: PrimaryDB,
  input: {
    runId?: string;
    documentId: number;
    reasonCode: string;
    candidates: Array<Record<string, unknown>>;
    confidence?: number | null;
  },
): Promise<string | null> {
  if (!input.runId) return null;
  const taskId = generateULID();
  await db
    .insert(processing_review_tasks)
    .values({
      id: taskId,
      run_id: input.runId,
      document_id: input.documentId,
      scope: "route",
      scope_id: "document",
      reason_code: input.reasonCode,
      candidates: input.candidates,
      confidence: input.confidence ?? null,
      status: "open",
      createdAt: new Date(),
      updatedAt: new Date(),
    })
    .onConflictDoNothing({
      target: [
        processing_review_tasks.run_id,
        processing_review_tasks.scope,
        processing_review_tasks.scope_id,
      ],
    });
  const [stored] = await db
    .select({ id: processing_review_tasks.id })
    .from(processing_review_tasks)
    .where(
      and(
        eq(processing_review_tasks.run_id, input.runId),
        eq(processing_review_tasks.scope, "route"),
        eq(processing_review_tasks.scope_id, "document"),
      ),
    )
    .limit(1);
  return stored?.id ?? null;
}

export async function resolveRoutingReviewTask(
  db: PrimaryDB,
  input: {
    runId: string;
    taskId: string;
    actorUserId: string;
    tableId: string;
    note?: string;
  },
): Promise<boolean> {
  const now = new Date();
  const [claimed] = await db
    .update(processing_review_tasks)
    .set({ status: "resolved", resolvedAt: now, updatedAt: now })
    .where(
      and(
        eq(processing_review_tasks.id, input.taskId),
        eq(processing_review_tasks.run_id, input.runId),
        eq(processing_review_tasks.status, "open"),
      ),
    )
    .returning({ id: processing_review_tasks.id });
  if (!claimed) return false;
  await db.batch([
    db.insert(processing_review_decisions).values({
      id: generateULID(),
      review_task_id: input.taskId,
      run_id: input.runId,
      actor_user_id: input.actorUserId,
      decision: { kind: "route_to_table", tableId: input.tableId },
      note: input.note,
      createdAt: now,
    }),
    db
      .update(processing_runs)
      .set({
        status: "running",
        current_stage: "segment",
        lastHeartbeatAt: now,
        updatedAt: now,
      })
      .where(eq(processing_runs.id, input.runId)),
  ]);
  return true;
}

export async function reopenRoutingReviewTask(
  db: PrimaryDB,
  input: { runId: string; taskId: string; failureMessage: string },
): Promise<void> {
  const now = new Date();
  await db.batch([
    db
      .update(processing_review_tasks)
      .set({ status: "open", resolvedAt: null, updatedAt: now })
      .where(
        and(
          eq(processing_review_tasks.id, input.taskId),
          eq(processing_review_tasks.run_id, input.runId),
          eq(processing_review_tasks.status, "resolved"),
        ),
      ),
    db
      .update(processing_runs)
      .set({
        status: "waiting_for_review",
        current_stage: "route",
        failure_code: "EXTRACTION_DISPATCH_FAILED",
        failure_message: input.failureMessage,
        lastHeartbeatAt: now,
        completedAt: null,
        updatedAt: now,
      })
      .where(eq(processing_runs.id, input.runId)),
  ]);
}
